import React, { useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD, BLUES } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const SOURCE_LABELS = { widget: 'Booking widget', getmyboat: 'GetMyBoat', 'legacy-import': 'Imported', website: 'Website', direct: 'Direct', 'internal-custom': 'Internal', gmb_email: 'GetMyBoat', fareharbor: 'FareHarbor', migration: 'Imported', bookingflows: 'BookingFlows', admin: 'Added by team', internal: 'Internal', 'widget-embed': 'Booking widget' };

export default function ServiceSalesMixReport() {
  const [months, setMonths] = useState(12);
  const { data, loading } = useIntelligenceReport('service-sales-mix', months);
  const { formatMoney } = useOperatorLocale();

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['Service', 'Revenue', 'Share %', 'Bookings', 'Avg Price', 'Avg Party Size', 'Add-on Revenue', 'Add-on Attach %', 'Avg Lead Days'],
      ...data.services.map((s) => [s.name, s.revenue, s.share, s.bookings, s.avgPrice, s.avgPartySize ?? '', s.addOnRevenue, s.addOnAttachRate, s.avgLeadDays ?? '']),
    ], `service-sales-mix-${data.periodStart}-to-${data.periodEnd}.csv`);
  };

  const s = data?.summary;
  const topServices = (data?.services || []).filter((x) => x.revenue > 0).slice(0, 8);

  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: `service-sales-mix-${data.periodStart}-to-${data.periodEnd}.pdf`,
      title: 'Revenue Mix',
      subtitle: `Your bookings only · ${data.periodStart} to ${data.periodEnd}`,
      kpis: [
        ['Total revenue', formatMoney(s.totalRevenue)],
        ['Bookings', String(s.totalBookings)],
        ['Avg party size', String(s.avgPartySize ?? '-')],
        ['Add-on revenue', `${formatMoney(s.addOnRevenue)} (${s.addOnAttachRate}% attach)`],
        ['Avg lead time', s.avgLeadDays != null ? `${s.avgLeadDays} days` : '-'],
      ],
      tables: [
        {
          title: 'Services',
          head: ['Service', 'Revenue', 'Share %', 'Bookings', 'Avg price', 'Avg party', 'Add-on %', 'Lead days'],
          rows: data.services.map((sv) => [sv.name, formatMoney(sv.revenue), sv.share, sv.bookings, formatMoney(sv.avgPrice), sv.avgPartySize ?? '-', sv.addOnAttachRate, sv.avgLeadDays ?? '-']),
        },
        {
          title: 'Booking sources',
          head: ['Source', 'Bookings', 'Revenue'],
          rows: data.sources.map((src) => [SOURCE_LABELS[src.source] || src.source, src.bookings, formatMoney(src.revenue)]),
        },
      ],
    });
  };

  return (
    <IntelligenceShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="service-sales-mix-report">
        <ReportHeader
          title="Revenue Mix"
          blurb={`Which of your services actually sell over the last ${months} months — realized prices, party sizes, add-on attach and how far ahead customers book each one.`}
          right={<><AiSuggestion report="service-sales-mix" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="service-mix" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="service-mix-export" /></>}
        />

        {loading ? <ReportSkeleton /> : !data ? null : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <KpiCard delay={60} testid="kpi-mix-revenue" label="Total revenue" value={formatMoney(s.totalRevenue)} sub={`${s.totalBookings} bookings · ${s.activeServices} selling services`} />
              <KpiCard delay={120} testid="kpi-mix-party" label="Avg party size" value={s.avgPartySize ?? '—'} sub="guests per booking" />
              <KpiCard delay={180} testid="kpi-mix-addons" label="Add-on revenue" value={formatMoney(s.addOnRevenue)} sub={`attached to ${s.addOnAttachRate}% of bookings`} />
              <KpiCard delay={240} testid="kpi-mix-lead" label="Avg lead time" value={s.avgLeadDays != null ? `${s.avgLeadDays} days` : '—'} sub="booked ahead of trip" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
              <ChartCard delay={300} title="Revenue by service" testid="service-mix-chart" className="lg:col-span-2">
                <div style={{ height: Math.max(200, topServices.length * 44) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topServices} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [formatMoney(v), 'Revenue']} contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                      <Bar dataKey="revenue" radius={[0, 8, 8, 0]} barSize={20}>
                        {topServices.map((_, i) => <Cell key={i} fill={BLUES[i % BLUES.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard delay={360} title="Where bookings come from" testid="service-mix-sources">
                <div className="space-y-3.5">
                  {data.sources.map((src) => {
                    const pct = s.totalRevenue ? Math.round(100 * src.revenue / s.totalRevenue) : 0;
                    return (
                      <div key={src.source}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="font-semibold text-slate-700">{SOURCE_LABELS[src.source] || src.source}</span>
                          <MonoNum className="text-slate-500 text-xs">{src.bookings} · {formatMoney(src.revenue)}</MonoNum>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  {data.sources.length === 0 && <p className="text-sm text-slate-400">No bookings in this period.</p>}
                </div>
              </ChartCard>
            </div>

            <div className={`${CARD} overflow-x-auto intel-rise`} style={{ animationDelay: '420ms' }} data-testid="service-mix-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    {['Service', 'Revenue', 'Share', 'Bookings', 'Avg price', 'Avg party', 'Add-on attach', 'Lead time'].map((h) => (
                      <th key={h} className="px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.services.map((sv) => (
                    <tr key={sv.name} className="border-b border-slate-50 last:border-0 hover:bg-[#F7F9FC] transition-colors duration-150">
                      <td className="px-6 py-4 font-bold text-slate-900">{sv.name}</td>
                      <td className="px-6 py-4"><MonoNum className="font-bold text-slate-900">{formatMoney(sv.revenue)}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{sv.share}%</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{sv.bookings}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{formatMoney(sv.avgPrice)}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{sv.avgPartySize ?? '—'}</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{sv.addOnAttachRate}%</MonoNum></td>
                      <td className="px-6 py-4"><MonoNum className="text-slate-600">{sv.avgLeadDays != null ? `${sv.avgLeadDays}d ahead` : '—'}</MonoNum></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </IntelligenceShell>
  );
}
